import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Upload, Trash2, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const Field = ({ label, id, ...props }) => (
  <label className="block">
    <span className="text-xs font-mono uppercase tracking-widest text-[var(--muted)]">{label}</span>
    <input id={id} data-testid={id} {...props} className="mt-1 w-full rounded-xl border border-black/10 bg-white/80 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--saffron)]" />
  </label>
);

const readFile = (file) =>
  new Promise((resolve, reject) => {
    if (file.size > 500 * 1024) return reject(new Error("Image must be under 500KB"));
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const PlatformCard = ({ id, label, icon, value, onChange }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const dataUrl = await readFile(f);
      onChange({ ...value, qr_image: dataUrl, qr_mode: "upload" });
      toast.success("QR image added");
    } catch (err) {
      toast.error(err.message);
    }
  };
  return (
    <div className="glass rounded-3xl p-6 space-y-4" data-testid={`download-card-${id}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-display text-2xl text-[var(--ink)] flex items-center gap-2"><i className={icon} aria-hidden="true" /> {label}</h3>
        <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
          <input type="checkbox" checked={value.coming_soon} onChange={(e) => set("coming_soon", e.target.checked)} data-testid={`${id}-coming-soon`} />
          Coming soon
        </label>
      </div>
      <Field label="Store URL" id={`${id}-url`} value={value.url} onChange={(e) => set("url", e.target.value)} placeholder="https://" />
      <Field label="Requirement note" id={`${id}-note`} value={value.note} onChange={(e) => set("note", e.target.value)} placeholder="Requires Android 8.0+" />
      <div>
        <span className="text-xs font-mono uppercase tracking-widest text-[var(--muted)]">QR code</span>
        <div className="mt-2 inline-flex p-1 rounded-full bg-black/[0.06]">
          {[["auto", "Auto from URL"], ["upload", "Uploaded image"]].map(([m, l]) => (
            <button key={m} type="button" onClick={() => set("qr_mode", m)} data-testid={`${id}-qr-mode-${m}`}
              className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${value.qr_mode === m ? "bg-[var(--ink)] text-white" : "text-[var(--ink-soft)]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-start gap-5">
        <div className="p-3 rounded-2xl bg-white border border-black/5" data-testid={`${id}-qr-preview`}>
          {value.qr_mode === "upload" && value.qr_image ? (
            <img src={value.qr_image} alt="Uploaded QR" className="w-[132px] h-[132px] object-contain" />
          ) : (
            <QRCodeSVG value={value.url || "https://trucitizen.in"} size={132} level="H" imageSettings={{ src: "/logo.png", height: 18, width: 52, excavate: true }} />
          )}
        </div>
        {value.qr_mode === "upload" && (
          <div className="space-y-2">
            <label className="btn-ghost !py-2 !px-4 text-sm cursor-pointer">
              <Upload size={14} /> Upload PNG/JPG
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid={`${id}-qr-file`} />
            </label>
            {value.qr_image && (
              <button type="button" onClick={() => set("qr_image", "")} className="flex items-center gap-1 text-xs text-red-600" data-testid={`${id}-qr-remove`}><Trash2 size={12} /> Remove image</button>
            )}
            <p className="text-xs text-[var(--muted)]">Max 500KB. Square image recommended.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export const DownloadSettings = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/settings/download").then((r) => setS(r.data)); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/download", s);
      setS(data);
      toast.success("App download settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!s) return <div className="h-64 rounded-3xl bg-white/50 animate-pulse" data-testid="download-settings-loading" />;
  return (
    <div className="space-y-6" data-testid="download-settings">
      <div className="grid lg:grid-cols-2 gap-6">
        <PlatformCard id="android" label="Android" icon="fa-brands fa-android" value={s.android} onChange={(v) => setS({ ...s, android: v })} />
        <PlatformCard id="ios" label="iOS" icon="fa-brands fa-apple" value={s.ios} onChange={(v) => setS({ ...s, ios: v })} />
      </div>
      <button onClick={save} disabled={busy} className="btn-saffron !py-2.5 !px-6 text-sm" data-testid="download-settings-save"><Save size={16} /> {busy ? "Saving…" : "Save changes"}</button>
    </div>
  );
};
